/**
 * Predictive search. Queries Shopify's own /search/suggest endpoint and
 * renders only what the store actually contains — products, pages and
 * articles. Nothing is fabricated, and an empty query shows nothing.
 *
 * Keyboard model: the input is a combobox, results are a listbox. Arrow
 * keys move aria-activedescendant rather than focus, so the visitor keeps
 * typing while browsing results; Enter opens the highlighted one and
 * Escape returns to the plain form.
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 220;
  var MIN_LENGTH = 2;

  function init(root) {
    root.querySelectorAll('[data-predictive-search-form]').forEach(function (form) {
      if (form.dataset.predictiveInit) return;
      form.dataset.predictiveInit = 'true';

      var input = form.querySelector('[data-predictive-search-input]');
      var panel = form.parentElement.querySelector('[data-predictive-search-results]');
      var status = form.parentElement.querySelector('[data-predictive-search-status]');
      if (!input || !panel) return;

      var timer = null;
      var controller = null;
      var activeIndex = -1;

      function options() {
        return Array.prototype.slice.call(panel.querySelectorAll('[role="option"]'));
      }

      function setActive(index) {
        var items = options();
        if (!items.length) return;
        activeIndex = (index + items.length) % items.length;
        items.forEach(function (item, i) {
          var active = i === activeIndex;
          item.classList.toggle('is-active', active);
          if (active) {
            input.setAttribute('aria-activedescendant', item.id);
            item.scrollIntoView({ block: 'nearest' });
          }
        });
      }

      function close() {
        panel.hidden = true;
        panel.innerHTML = '';
        activeIndex = -1;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
      }

      function announce(count) {
        if (!status) return;
        status.textContent = count
          ? count + ' results available'
          : 'No results found';
      }

      function search(query) {
        if (controller) controller.abort();
        controller = new AbortController();

        var url =
          (window.theme && window.theme.routes.predictive_search_url ? window.theme.routes.predictive_search_url : '/search/suggest') +
          '?q=' + encodeURIComponent(query) +
          '&resources[type]=product,page,article' +
          '&resources[limit]=6' +
          '&resources[options][unavailable_products]=last' +
          '&section_id=predictive-search';

        fetch(url, { signal: controller.signal })
          .then(function (response) {
            if (!response.ok) throw new Error('Search failed');
            return response.text();
          })
          .then(function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var results = parsed.querySelector('[data-predictive-search-markup]');
            if (!results) {
              close();
              return;
            }
            panel.innerHTML = results.innerHTML;
            panel.hidden = false;
            input.setAttribute('aria-expanded', 'true');
            activeIndex = -1;
            announce(options().length);
          })
          .catch(function (error) {
            if (error.name === 'AbortError') return;
            close();
          });
      }

      input.addEventListener('input', function () {
        var query = input.value.trim();
        clearTimeout(timer);
        if (query.length < MIN_LENGTH) {
          close();
          return;
        }
        timer = setTimeout(function () { search(query); }, DEBOUNCE_MS);
      });

      input.addEventListener('keydown', function (event) {
        if (panel.hidden) return;
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setActive(activeIndex + 1);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          setActive(activeIndex - 1);
        } else if (event.key === 'Enter' && activeIndex > -1) {
          var current = options()[activeIndex];
          if (current) {
            event.preventDefault();
            window.location.href = current.href || current.dataset.url;
          }
        } else if (event.key === 'Escape') {
          close();
        }
      });

      panel.addEventListener('mousemove', function (event) {
        var option = event.target.closest('[role="option"]');
        if (!option) return;
        setActive(options().indexOf(option));
      });

      document.addEventListener('click', function (event) {
        if (!form.parentElement.contains(event.target)) close();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
